import React, { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { SectionLabel, MaskedHeading, Reveal } from "@/components/site/Motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createApplication } from "@/lib/api";
import { translations } from "@/i18n/translations";

const POSITION_KEYS = translations.en.careers.positions;

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-brand text-white placeholder:text-white/35 outline-none py-3 transition-colors font-body";
const labelCls = "text-xs uppercase tracking-widest text-white/55 mb-2 block font-body";

const CAREERS_IMG =
  "https://images.unsplash.com/photo-1517774729239-58be8e8875cc?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export const Careers = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", age: "", experience: "", notes: "" });
  const [position, setPosition] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !position) {
      toast.error(t.careers.invalid);
      return;
    }
    setLoading(true);
    try {
      await createApplication({
        name: form.name,
        phone: form.phone,
        position,
        age: form.age ? parseInt(form.age, 10) : null,
        experience: form.experience || null,
        notes: form.notes || null,
      });
      toast.success(t.careers.success, { icon: <Check size={16} /> });
      setForm({ name: "", phone: "", age: "", experience: "", notes: "" });
      setPosition("");
    } catch {
      toast.error(t.careers.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="careers" className="py-24 md:py-36 px-6 md:px-12 bg-surface/30 border-y border-white/10">
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Image */}
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative overflow-hidden h-[360px] md:h-[520px]">
            <img src={CAREERS_IMG} alt="Join VIP1" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
          </div>
        </Reveal>

        {/* Form */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <SectionLabel>{t.careers.overline}</SectionLabel>
          <h2 className="font-heading text-4xl md:text-6xl text-white tracking-tight leading-[1.02]">
            <MaskedHeading text={t.careers.title} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-5 mb-10 font-body text-white/60 leading-relaxed max-w-xl">{t.careers.desc}</p>
          </Reveal>

          <Reveal delay={0.15}>
            <form data-testid="careers-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className={labelCls}>{t.careers.name} *</label>
                <input data-testid="job-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="—" />
              </div>
              <div>
                <label className={labelCls}>{t.careers.phone} *</label>
                <input data-testid="job-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+90 ___ ___ __ __" />
              </div>

              <div>
                <label className={labelCls}>{t.careers.position} *</label>
                <Select value={position} onValueChange={setPosition}>
                  <SelectTrigger
                    data-testid="job-position"
                    className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-auto py-3 text-white focus:ring-0 focus:border-brand data-[placeholder]:text-white/35"
                  >
                    <SelectValue placeholder={t.careers.pickPosition} />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/15 text-white">
                    {t.careers.positions.map((p, i) => (
                      <SelectItem key={POSITION_KEYS[i]} value={POSITION_KEYS[i]} className="focus:bg-brand/20 focus:text-white">
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>{t.careers.age}</label>
                <input data-testid="job-age" type="number" min="18" max="99" value={form.age} onChange={set("age")} className={inputCls} placeholder="—" />
              </div>

              <div className="md:col-span-2">
                <label className={labelCls}>{t.careers.experience}</label>
                <input data-testid="job-experience" value={form.experience} onChange={set("experience")} className={inputCls} placeholder={t.careers.experiencePh} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>{t.careers.notes}</label>
                <input data-testid="job-notes" value={form.notes} onChange={set("notes")} className={inputCls} placeholder={t.careers.notesPh} />
              </div>

              <div className="md:col-span-2 mt-2">
                <button
                  data-testid="careers-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-brand hover:bg-brand-light text-white font-body px-10 py-4 transition-colors disabled:opacity-60 hover:scale-[1.02] duration-300"
                >
                  {loading ? t.careers.submitting : t.careers.submit}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
